import React, { useState, useEffect } from 'react';
import './ApiConnectionTest.css';

// Fonction pour obtenir l'URL de l'API dynamiquement
const getApiUrlDynamic = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  return 'http://localhost:8000/api';
};

const ApiConnectionTest = () => {
  const [connectionStatus, setConnectionStatus] = useState('testing');
  const [lastTest, setLastTest] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const testApiConnection = async () => {
    setIsLoading(true);
    setConnectionStatus('testing');
    
    try {
      const apiUrl = getApiUrlDynamic();
      
      // Test de l'endpoint de santé
      const healthResponse = await fetch(`${apiUrl}/health`);
      const healthData = await healthResponse.json();
      
      // Test de l'endpoint de test
      const testResponse = await fetch(`${apiUrl}/test`);
      const testData = await testResponse.json();
      
      if (healthResponse.ok && testResponse.ok) {
        setConnectionStatus('connected');
        setTestResults({
          health: healthData,
          test: testData,
          timestamp: new Date().toLocaleString('fr-FR')
        });
      } else {
        setConnectionStatus('error');
        setTestResults({
          error: 'Erreur de réponse API',
          health: healthData,
          test: testData,
          timestamp: new Date().toLocaleString('fr-FR')
        });
      }
    } catch (error) {
      setConnectionStatus('error');
      setTestResults({
        error: error.message,
        timestamp: new Date().toLocaleString('fr-FR')
      });
    } finally {
      setIsLoading(false);
      setLastTest(new Date().toLocaleString('fr-FR'));
    }
  };

  useEffect(() => {
    // Test automatique au chargement du composant
    testApiConnection();
  }, []);

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return '✅';
      case 'error':
        return '❌';
      case 'testing':
        return '🔄';
      default:
        return '❓';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connecté';
      case 'error':
        return 'Erreur de connexion';
      case 'testing':
        return 'Test en cours...';
      default:
        return 'Non testé';
    }
  };

  const getStatusClass = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'status-connected';
      case 'error':
        return 'status-error';
      case 'testing':
        return 'status-testing';
      default:
        return 'status-unknown';
    }
  };

  return (
    <div className="api-connection-test">
      <div className="api-test-header">
        <h3 className="api-test-title">Test de connexion API</h3>
        <div className={`api-status ${getStatusClass()}`}>
          <span className="status-icon">{getStatusIcon()}</span>
          <span className="status-text">{getStatusText()}</span>
        </div>
      </div>

      <div className="api-test-actions">
        <button 
          className="test-btn"
          onClick={testApiConnection}
          disabled={isLoading}
        >
          {isLoading ? 'Test en cours...' : 'Tester la connexion'}
        </button>
        
        {lastTest && (
          <span className="last-test">
            Dernier test : {lastTest}
          </span>
        )}
      </div>

      {testResults && (
        <div className="api-test-results">
          <h4>Résultats du test :</h4>
          
          {testResults.error ? (
            <div className="error-result">
              <strong>Erreur :</strong> {testResults.error}
            </div>
          ) : (
            <div className="success-results">
              <div className="result-section">
                <h5>Endpoint /health :</h5>
                <pre>{JSON.stringify(testResults.health, null, 2)}</pre>
              </div>
              
              <div className="result-section">
                <h5>Endpoint /test :</h5>
                <pre>{JSON.stringify(testResults.test, null, 2)}</pre>
              </div>
            </div>
          )}
          
          <div className="test-timestamp">
            Test effectué le : {testResults.timestamp}
          </div>
        </div>
      )}

      <div className="api-info">
        <h4>Informations de connexion :</h4>
        <p><strong>URL API :</strong> {getApiUrlDynamic()}</p>
        <p><strong>Endpoints testés :</strong></p>
        <ul>
          <li><code>GET /api/health</code> - Vérification de l'état de l'API</li>
          <li><code>GET /api/test</code> - Test de fonctionnement général</li>
        </ul>
      </div>
    </div>
  );
};

export default ApiConnectionTest;
